// Program model helpers — mirror ExerciseProgramModel / ProgramExerciseEntry in Flutter.
import { DAY_KEYS } from '../i18n';

export function normalizeDayKey(key) {
  if (typeof key === 'number') return key >= 1 && key <= 7 ? DAY_KEYS[key - 1] : null;
  if (typeof key !== 'string') return null;
  const lower = key.toLowerCase();
  if (DAY_KEYS.includes(lower)) return lower;
  const n = parseInt(key, 10);
  if (String(n) === key && n >= 1 && n <= 7) return DAY_KEYS[n - 1];
  return null;
}

const asInt = (v) => {
  if (typeof v === 'number') return Math.trunc(v);
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
};

/** Parses one Firestore entry (map or legacy "id,sets,reps" string). */
export function parseEntry(e) {
  if (e == null) return null;
  if (typeof e === 'string') {
    const parts = e.split(',');
    if (parts.length >= 3) {
      const id = parts[0].trim();
      const sets = asInt(parts[1].trim());
      const reps = asInt(parts[2].trim());
      if (id && sets != null && reps != null) return { exerciseId: id, sets, reps, durationMin: null, weight: null };
    }
    return null;
  }
  if (typeof e === 'object') {
    const id = e.exerciseId ?? e.exerciseID;
    if (!id) return null;
    const sets = asInt(e.sets);
    const reps = asInt(e.reps);
    if (sets == null || reps == null) return null;
    const dm = asInt(e.duration_min ?? e.durationMin);
    let weight = null;
    if (e.weight != null && e.weight !== '') {
      const w = typeof e.weight === 'number' ? e.weight : parseFloat(String(e.weight).replace(',', '.'));
      if (!Number.isNaN(w)) weight = w;
    }
    const name = typeof e.name === 'string' && e.name.trim() ? e.name.trim() : null;
    const muscle = typeof e.muscle === 'string' && e.muscle.trim() ? e.muscle.trim() : null;
    return { exerciseId: String(id), sets, reps, durationMin: dm && dm > 0 ? dm : null, weight, name, muscle };
  }
  return null;
}

export function toFirestoreEntry(e) {
  const out = {
    exerciseId: e.exerciseId,
    sets: e.sets,
    reps: e.reps,
    duration_min: e.durationMin ?? null,
    weight: e.weight ?? null,
  };
  if (e.name) {
    out.name = e.name;
    if (e.muscle) out.muscle = e.muscle;
  }
  return out;
}

/* Custom exercises: typed in by the user, not in exercises.json. They live only
 * inside a program entry (`custom_…` id plus inline `name` / optional `muscle`). */
export const CUSTOM_ID_PREFIX = 'custom_';

export const isCustomExercise = (ex) => !!ex && String(ex.id || '').startsWith(CUSTOM_ID_PREFIX);

export function newCustomExercise(name, muscle) {
  return { id: `${CUSTOM_ID_PREFIX}${Date.now()}`, name: name.trim(), muscle: muscle || '', equipment: '', imagePath: '', videoPath: '' };
}

/** Catalog-shaped exercise for a custom entry, or null for catalog entries. */
export function customExerciseFor(entry) {
  if (!entry?.name) return null;
  return { id: entry.exerciseId, name: entry.name, muscle: entry.muscle || '', equipment: '', imagePath: '', videoPath: '' };
}

/** Catalog exercise for an entry, falling back to its inline custom data. */
export function resolveExercise(byId, entry) {
  return (entry && byId[entry.exerciseId]) || customExerciseFor(entry);
}

/** Entry fields that identify `exercise` (custom ones carry their name/muscle). */
function identityOf(exercise) {
  const custom = isCustomExercise(exercise);
  return { exerciseId: exercise.id, name: custom ? exercise.name : null, muscle: custom && exercise.muscle ? exercise.muscle : null };
}

/** Firestore `program` map → { monday: [entry…], … } with normalized keys. */
export function parseProgram(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.entries(raw).forEach(([k, v]) => {
    const day = normalizeDayKey(k);
    if (!day || !Array.isArray(v)) return;
    out[day] = v.map(parseEntry).filter(Boolean);
  });
  return out;
}

export function serializeProgram(program) {
  const out = {};
  Object.entries(program).forEach(([day, list]) => {
    out[day.toLowerCase()] = list.map(toFirestoreEntry);
  });
  return out;
}

export function sortDays(days) {
  return [...days].sort((a, b) => DAY_KEYS.indexOf(a) - DAY_KEYS.indexOf(b));
}

export function dayHasWorkout(program, weekdayIndex) {
  if (!program) return false;
  const list = program[DAY_KEYS[weekdayIndex]];
  return !!(list && list.length);
}

export function countExercises(program) {
  return Object.values(program || {}).reduce((s, l) => s + l.length, 0);
}

export function defaultEntryFor(exercise) {
  const isCardio = String(exercise.muscle || '').trim().toLowerCase() === 'cardio';
  return isCardio
    ? { ...identityOf(exercise), sets: 1, reps: 1, durationMin: 20, weight: null }
    : { ...identityOf(exercise), sets: 3, reps: 12, durationMin: null, weight: null };
}

/** Same targets, different exercise (replace). */
export function withExercise(entry, exercise) {
  return { ...entry, ...identityOf(exercise) };
}

export function formatWeight(w) {
  if (w == null) return '';
  return Number.isInteger(w) ? String(w) : String(Math.round(w * 10) / 10);
}

/** Rough seconds per set (work + rest) when estimating a day's length. */
const SECONDS_PER_SET = 120;

/** Estimated minutes for a day (mirrors estimateProgramDayMinutes in Flutter). */
export function estimateDayMinutes(list) {
  const seconds = (list || []).reduce(
    (s, e) => s + (e.durationMin > 0 ? e.durationMin * 60 : e.sets * SECONDS_PER_SET),
    0,
  );
  return Math.round(seconds / 60);
}

const FOLD = { İ: 'i', I: 'i', ı: 'i', Ş: 's', ş: 's', Ğ: 'g', ğ: 'g', Ü: 'u', ü: 'u', Ö: 'o', ö: 'o', Ç: 'c', ç: 'c' };

/** Lowercase without Turkish diacritics, so "gogus" matches "Göğüs". */
export function foldForSearch(s) {
  return String(s || '').replace(/[İIıŞşĞğÜüÖöÇç]/g, (c) => FOLD[c]).toLowerCase();
}

/**
 * Appends entries to `day` in a cloned program (creating the day if it was a
 * rest day), skipping exercises the day already has. Returns the added count.
 */
export function appendEntries(program, day, entries) {
  const list = program[day] || (program[day] = []);
  let added = 0;
  entries.forEach((entry) => {
    if (list.some((e) => e.exerciseId === entry.exerciseId)) return;
    list.push({ ...entry });
    added += 1;
  });
  return added;
}
